import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { provideRouter, TitleStrategy, withComponentInputBinding } from '@angular/router';
import { TranslateTitleStrategy } from '@core/services/translate-title.strategy';
import { routes } from './app.routes';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeuix/themes/aura';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { environment } from '../environments/environment';
import { telekomPreset } from '../theme/telekom-preset';
import { definePreset } from '@primeuix/themes';
import { MSAL_INSTANCE, MsalBroadcastService, MsalGuard, MsalService } from '@azure/msal-angular';
import { BrowserCacheLocation, PublicClientApplication } from '@azure/msal-browser';
import { AuthService } from '@core/services/auth.service';
import { AuthInterceptor } from '@core/interceptors/auth.interceptor';
import { ErrorInterceptor } from '@core/interceptors/error.interceptor';

const customPreset = definePreset(Aura, telekomPreset);

const i18nSuffix = '.json?v=' + Date.now();

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideHttpClient(withFetch(), withInterceptorsFromDi()),
    provideRouter(routes, withComponentInputBinding()),
    { provide: TitleStrategy, useClass: TranslateTitleStrategy },
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: '../assets/i18n/',
        suffix: i18nSuffix,
      }),
      fallbackLang: 'en',
    }),
    providePrimeNG({
      theme: {
        preset: customPreset,
        options: {
          darkModeSelector: false,
        },
      },
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: () =>
        new PublicClientApplication({
          auth: {
            clientId: environment.msal.clientId,
            authority: environment.msal.authority,
            redirectUri: window.location.origin,
            postLogoutRedirectUri: window.location.origin,
          },
          cache: {
            cacheLocation: BrowserCacheLocation.LocalStorage,
          },
        }),
    },
    MsalBroadcastService,
    MsalGuard,
    MsalService,
    AuthService,
    provideAppInitializer(() => {
      const svc = inject(AuthService);
      return svc.loadConfigs();
    }),
  ],
};
